// ─────────────────────────────────────────────────────────────────────────────
// Registro de push NATIVO (Capacitor / Android).
//
// En la app nativa, el token FCM lo entrega el sistema operativo (no el navegador).
// Lo guardamos en users/{uid}.fcmToken — el MISMO campo que ya leen las Cloud
// Functions (notificarPedidoAsignado, etc.), así que no hay que tocar la lógica
// de envío: apenas el operario tenga token nativo, las notificaciones existentes
// empiezan a llegarle.
//
// En la web (navegador normal) esto es un no-op: Capacitor.isNativePlatform() es
// false y salimos sin hacer nada, así que no rompe la PWA ni el build web.
// ─────────────────────────────────────────────────────────────────────────────
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../firebase";

let listenersReady = false;
let currentUid = null;
let currentNavigate = null;

async function guardarToken(value) {
  if (!currentUid || !value) return;
  try {
    await setDoc(
      doc(db, "users", currentUid),
      { fcmToken: value, fcmPlataforma: "android", fcmActualizado: Date.now() },
      { merge: true }
    );
    console.log("[push] token nativo guardado");
  } catch (e) {
    console.error("[push] no se pudo guardar el token", e);
  }
}

function navegarDesdeNotificacion(data = {}) {
  const link = data.link || data.click_action || data.path;
  if (!link || !currentNavigate) return;
  try {
    // link puede venir absoluto (https://app.urualum.uy/pedidos-operario/123)
    // o relativo (/pedidos-operario/123).
    if (typeof link === "string" && link.startsWith("/")) {
      currentNavigate(link);
    } else {
      const u = new URL(link);
      currentNavigate(u.pathname + u.search);
    }
  } catch {
    /* link inválido — no navegamos */
  }
}

function registrarListeners() {
  if (listenersReady) return;
  listenersReady = true;

  // Token obtenido (o refrescado) → guardarlo
  PushNotifications.addListener("registration", (token) => {
    guardarToken(token?.value);
  });

  PushNotifications.addListener("registrationError", (err) => {
    console.error("[push] registrationError", err);
  });

  // El usuario tocó la notificación → llevarlo al pedido
  PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
    navegarDesdeNotificacion(action?.notification?.data);
  });
}

/**
 * Inicializa el push nativo. Llamar cuando el usuario está logueado.
 * Seguro de llamar varias veces y en la web (ahí no hace nada).
 *
 * @param {{ uid?: string|null, navigate?: (path: string) => void }} opts
 */
export async function initNativePush({ uid, navigate } = {}) {
  if (!Capacitor.isNativePlatform()) return; // solo app nativa
  if (uid) currentUid = uid;
  if (navigate) currentNavigate = navigate;
  if (!currentUid) return;

  try {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== "granted") {
      console.warn("[push] permiso de notificaciones no concedido");
      return;
    }

    registrarListeners();
    // Dispara el evento 'registration' con el token
    await PushNotifications.register();
  } catch (e) {
    console.error("[push] error inicializando push nativo", e);
  }
}
